import {Injectable} from "@angular/core";
import {Router} from "@angular/router";
import {TranslateService} from "@ngx-translate/core";
import {ToastrService} from 'ngx-toastr';

import swal from "sweetalert2";
import {LogLevel, Multiselect, SweetAlertMsg} from "../interfaces/admin.interface";
import {SettingsService} from "../settings/settings.service";
import {LoggingService} from "./log.service";

/**
 * Service where all the utilities methods are stored
 *
 * (conversions, input checks, CRUD operations...)
 */
@Injectable()
export class UtilsService {

    private USER_SESSION_KEY = 'userInfo';
    private errorTranslation: any;

    constructor(
        private logService: LoggingService,
        private toasterService: ToastrService,
        private settingsService: SettingsService,
        private translate: TranslateService,
        private router: Router,
    ) {
        this.errorTranslation = this.translate.get("error");
    }

    /** Method that converts a timestamp in a locale string (dd/mm/yyyy / mm/dd/yyyy) */
    convertTimestampToDate(timestamp: number | string): string {
        return new Date(+timestamp).toLocaleString('it-IT', {day: '2-digit', month: '2-digit', year: 'numeric'});
    }

    /**
     * Method that manages the content that can be inserted in a number input (only numbers).
     *
     * Moreover the following input events are permitted (space, backspace, enter, tab),
     * in order to allow normal input features.
     */
    numberInputCheck(event: KeyboardEvent, float?: boolean): void {
        const keyCode = (event.code);
        if (
            keyCode
            && !keyCode.toLowerCase().includes('digit')
            && !(keyCode.toLowerCase().includes('numpad') && (!isNaN(+keyCode.toLowerCase().split('numpad')[1])))
            && !(keyCode.toLowerCase() === 'space')
            && !(keyCode.toLowerCase() === 'enter')
            && !(keyCode.toLowerCase() === 'backspace')
            && !(keyCode.toLowerCase() === 'tab')
            && !(float && (keyCode.toLowerCase() === 'period'))
        ) {
            event.preventDefault();
        }
    }

    /**
     * Method that manages what can be inserted in a percentage input form
     * @param event - Event that fired the check
     * @param percentageFormValue - Current value in the percentage input
     */
    percentageInputCheck(event: KeyboardEvent, percentageFormValue: number): void {
        const keyCode = (event.code);

        this.numberInputCheck(event);

        if (percentageFormValue + (+keyCode.toLowerCase().split('digit')[1]) > 100) {
            event.preventDefault();
        }
    }

    /** Method that checks the validity of an email input value */
    emailInputCheck(inputValue: string): boolean {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inputValue);
    }

    // ------------ SERVICES CRUD OPERATIONS ------------ //

    /**
     * Method that manages the create action of a new element
     * @param service - service that has to be used to execute the create action
     * @param createMethod - string referring to the service method that executes the create action
     * @param reqBody - create request body
     * @param toastMessage - message content to be displayed on success or error
     * @param sweetAlert - Alert to display informations about the operation status
     */
    createElement(service: any, createMethod: string, reqBody: any, toastMessage: any, sweetAlert: SweetAlertMsg): Promise<any> {
        return new Promise((resolve, reject) => {
            service[createMethod](reqBody).subscribe(
                (response: any) => {
                    this.logService.log("[SUCCESS] Creazione elemento", response, LogLevel.DEBUG);
                    resolve(response);
                    swal.fire(sweetAlert.creationText, "", "success");
                },
                (error: any) => {
                    this.logService.log("[ERRROR] Creazione elemento", error, LogLevel.DEBUG);
                    reject(error);
                    toastMessage = error.description;
                    swal.fire(sweetAlert.warning, toastMessage.element, "warning");
                }
            );
        });
    }

    /**
     * Method that manages the edit action of a new element
     * @param service - service that has to be used to execute the create action
     * @param editMethod - string referring to the service method that executes the edit action
     * @param reqBody - create request body
     * @param toastMessage - message content to be displayed on success or error
     * @param sweetAlert - Alert to display informations about the operation status
     */
    editElement(service: any, editMethod: string, reqBody: any, toastMessage: any, sweetAlert: SweetAlertMsg): Promise<any> {
        return new Promise((resolve, reject) => {
            service[editMethod](reqBody).subscribe(
                (response: any) => {
                    this.logService.log("[SUCCESS] Modifica elemento", response, LogLevel.DEBUG);
                    swal.fire(sweetAlert.updatingText, "", "success");
                    resolve(response);
                },
                (error: any) => {
                    this.logService.log("[ERROR] Modifica elemento", error, LogLevel.DEBUG);
                    toastMessage = error.description;
                    swal.fire(sweetAlert.warning, toastMessage.element, "warning");
                    reject(error);
                }
            );
        });
    }

    /**
     * Method that manages the duplicate action on a table entry
     * @param service - service that has to be used to execute the duplicate action
     * @param duplicateMethod - string referring to the service method that executes the duplicate action
     * @param id - element id
     * @param toastMessage - message content to be displayed on success or error
     */
    duplicateElement(service: any, duplicateMethod: string, id: string | number, toastMessage: any): Promise<any> {
        return new Promise((resolve, reject) => {
            service[duplicateMethod](id).subscribe(
                (response: any) => {
                    this.logService.log("[SUCCESS] Duplicazione elemento", response, LogLevel.DEBUG);
                    this.toasterService.success(toastMessage.duplicate_ok);
                    resolve(response);
                },
                (error: any) => {
                    this.logService.log("[ERROR] Duplicazione società", null, LogLevel.DEBUG);
                    this.toasterService.error(toastMessage.duplicate_er, this.manageErrorMsg(error));
                    reject(error);
                }
            );
        });
    }

    /**
     * Method that manages the toggle action on a table entry
     * @param service - service that has to be used to execute the toggle action
     * @param toggleMethod - string referring to the service method that executes the toggle action
     * @param id - element id
     * @param toastMessage - message content to be displayed on success or error
     */
    toggleElementState(service: any, toggleMethod: string, id: string | number, toastMessage: any): Promise<any> {
        return new Promise((resolve, reject) => {
            service[toggleMethod](id).subscribe(
                (response: any) => {
                    this.logService.log("[SUCCESS] Modifica stato elemento", response, LogLevel.DEBUG);
                    this.toasterService.success(toastMessage.toggle_active_ok);
                    resolve(response);
                },
                (error: any) => {
                    this.logService.log("[ERROR] Modifica stato elemento", null, LogLevel.DEBUG);
                    this.toasterService.error(toastMessage.toggle_active_er, this.manageErrorMsg(error));
                    reject(error);
                }
            );
        });
    }

    /**
     * Method that manages the delete action on a table entry
     * @param service - service that has to be used to execute the delete action
     * @param deleteMethod - string referring to the service method that executes the delete action
     * @param id - element id
     * @param toastMessage - message content to be displayed on success or error
     */
    deleteElement(service: any, deleteMethod: string, id: string | number, toastMessage: any): Promise<any> {
        return new Promise((resolve, reject) => {
            service[deleteMethod](id).subscribe(
                (response: any) => {
                    this.logService.log("[SUCCESS] Eliminazione elemento", response, LogLevel.DEBUG);
                    this.toasterService.success(toastMessage.delete_ok);
                    resolve(response);
                },
                (error: any) => {
                    this.logService.log("[ERROR] Eliminazione elemento", error, LogLevel.DEBUG);
                    this.toasterService.error(toastMessage.delete_er, this.manageErrorMsg(error));
                    reject(error);
                }
            );
        });
    }

    /**
     * Method that manages the creation of filters based on multiselect items
     * @param multiselectItems - Array of objects selected by the multiselect component
     * @param key - string representing the object property that has to be passed as filter value
     */
    getFilterItemValues(multiselectItems: any, key: string) {
        return multiselectItems.map((item: any) => item[key]);
    }

    /**
     * Product categories could be created with a name of category - subcategory,
     * when calling the filtering API though, only the category name has to be passed.
     *
     * Because of that the name has to be split for every category entry in the param object
     * and then saved as just the category (element 0 of the split resulting list)
     */
    formatCategoryName(formattedCategory: any) {
        return formattedCategory.map((c: any) => c.itemName = c.itemName.split('-')[0].trim());
    }

    sessionExpired() {
        localStorage.removeItem(this.USER_SESSION_KEY);
        localStorage.removeItem('token');
    }

    /** Method that returns filters to be used with a specific API */
    getApiFilters(key: string) {
        let filters = {};
        switch (key) {
            case Multiselect.USER:
                filters = {
                    USER: {
                        email: null,
                        role: null,
                        name: null,
                        surname: null,
                        enabled: null,
                        company: null
                    },
                    ROLE: {
                        code: null,
                    },
                    HEADQUARTER: {
                        id: null,
                    },
                };
                break;
            default:
                break;
        }
        return filters;
    }

    manageErrorMsg(outcome: any) {
        this.errorTranslation = this.translate.instant("error");
        let message = "";
        switch (outcome.code) {
            case "0000":
                message = this.errorTranslation.e_0000;
                break;
            case "0001":
                message = this.errorTranslation.e_0001;
                break;
            case "0002":
                message = this.errorTranslation.e_0002;
                break;
            case "0003":
                message = this.errorTranslation.e_0003;
                break;
            case "0004":
                message = this.errorTranslation.e_0004;
                break;
            case "0005":
                this.sessionExpired();
                this.router.navigate(["/login/0"]);
                message = this.errorTranslation.e_0005;
                break;
            case "0006":
                message = this.errorTranslation.e_0006;
                break;
            case "0007":
                this.sessionExpired();
                this.router.navigate(["/login/0"]);
                message = this.errorTranslation.e_0007;
                break;
            case "0008":
                message = this.errorTranslation.e_0008;
                break;
            case "0009":
                message = this.errorTranslation.e_0009;
                break;
            case "0010":
                message = this.errorTranslation.e_0010;
                break;
            case "0011":
                message = this.errorTranslation.e_0011;
                break;
            case "0012":
                message = this.errorTranslation.e_0012;
                break;
            case "0013":
                message = this.errorTranslation.e_0013;
                break;
            case "0014":
                message = this.errorTranslation.e_0014;
                break;
            case "0015":
                message = this.errorTranslation.e_0015;
                break;
            case "0016":
                message = this.errorTranslation.e_0016;
                break;
            case "0017":
                message = this.errorTranslation.e_0017;
                break;
            case "0018":
                message = this.errorTranslation.e_0018;
                break;
            case "0019":
                message = this.errorTranslation.e_0019;
                break;
            case "0020":
                message = this.errorTranslation.e_0020;
                break;
            case "0021":
                message = this.errorTranslation.e_0021;
                break;
            case "0022":
                message = this.errorTranslation.e_0022;
                break;
            case "0023":
                message = this.errorTranslation.e_0023;
                break;
            case "0024":
                message = this.errorTranslation.e_0024;
                break;
            case "0025":
                message = this.errorTranslation.e_0025;
                break;
            case "0026":
                message = this.errorTranslation.e_0026;
                break;
            case "0027":
                message = this.errorTranslation.e_0027;
                break;
            case "0028":
                message = this.errorTranslation.e_0028;
                break;
            case "0029":
                message = this.errorTranslation.e_0029;
                break;
            case "0030":
                message = this.errorTranslation.e_0030;
                break;
            case "0031":
                message = this.errorTranslation.e_0031;
                break;
            case "SRVR":
                message = outcome.message;
                break;
            default:
                message = this.errorTranslation.generic_error;
                break;
        }
        return message;
    }

    static getUpdateSweetAlert() {
        const updateSweetAlert = {
            updatingText: null,
            errorText: null,
            warning: null,
        };
        return updateSweetAlert;
    }

    static getCreateSweetAlert() {
        const createSweetAlert = {
            creationText: null,
            errorText: null,
            warning: null,
        };
        return createSweetAlert;
    }

    static getSubmitSweetAlert() {
        const submitSweetAlert = {
            loadingText: null,
        };
        return submitSweetAlert;
    }

    loadAlertTranslation(translate: TranslateService, submitSweetAlert: any, createSweetAlert: any, updateSweetAlert: any): void {
        translate.stream("swal").subscribe((tr) => {
            submitSweetAlert.loadingText = tr.op_in_progress;

            createSweetAlert.creationText = tr.cr_ok;
            createSweetAlert.errorText = tr.cr_er;
            createSweetAlert.warning = tr.warning;

            updateSweetAlert.updatingText = tr.up_ok;
            updateSweetAlert.errorText = tr.up_er;
            updateSweetAlert.warning = tr.warning;
        });
    }

}
